import React, { useEffect, useState } from "react";
import { Input } from "../ui/input";
import { useAttivazione } from "./shared";
import { BarraSalvataggio } from "./casella";
import { FONDO_CODICE, HandNote, SPAZI } from "./brand";
import { Campo, Gruppo, Passi, RigaInterruttore } from "../widget/campi";

// Le interazioni che il widget scrive nel dataLayer, nell'ordine del server
// (services/dataLayerApp.js → DATALAYER_ACTIONS).
export const INTERAZIONI = [
  {
    id: "open",
    titolo: "Apre la chat",
    aiuto: "Dalla bolla, dalla pillola o da un suggerimento. Nella barra di ricerca, quando apre il pannello.",
  },
  {
    id: "auto_open",
    titolo: "La chat si apre da sola",
    aiuto: "Solo se nel Widget è accesa l'apertura automatica.",
  },
  {
    id: "chat_started",
    titolo: "Scrive il primo messaggio",
    aiuto: "Una volta per conversazione: dopo mezz'ora senza messaggi conta come una nuova.",
  },
  {
    id: "internal_link_click",
    titolo: "Apre una pagina del sito",
    aiuto: "Un link suggerito dall'assistente verso il tuo sito. Arriva anche il percorso della pagina.",
  },
  {
    id: "whatsapp_click",
    titolo: "Passa a WhatsApp",
    aiuto: "Un clic che apre WhatsApp dal widget.",
  },
];

// Le tre variabili del push: il cliente ne sceglie i nomi, i valori li decide la piattaforma.
const VARIABILI = [
  { chiave: "action", etichetta: "Variabile dell'azione", esempio: "open, whatsapp_click…" },
  { chiave: "destination_path", etichetta: "Variabile del percorso", esempio: "/offerte/" },
  { chiave: "position", etichetta: "Variabile della posizione", esempio: "floating_widget o search_bar" },
];
const NOMI_PREDEFINITI = { action: "action", destination_path: "destination_path", position: "position" };

// Gli stessi controlli del server (services/dataLayerApp.js).
const NOME_VALIDO = /^[A-Za-z][A-Za-z0-9_]*$/;
const PARAMETRO_VALIDO = /^[A-Za-z][A-Za-z0-9_-]*$/;
const VALORE_VALIDO = /^[A-Za-z0-9][A-Za-z0-9_.-]*$/;
const PARAMETRI_RISERVATI = /^(utm_.*|gclid|gbraid|wbraid|dclid|fbclid|msclkid)$/i;

// Come nella mail di Webranking: lo stesso evento per tutte, le distingue "action".
const EVENTI_PREDEFINITI = Object.fromEntries(INTERAZIONI.map((i) => [i.id, "pingup_interaction"]));
// Le etichette dei campi dei nomi, sotto ogni interruttore (diverse dal suo titolo).
const EVENTO_DI = {
  open: "Evento dell'apertura",
  auto_open: "Evento dell'apertura automatica",
  chat_started: "Evento del primo messaggio",
  internal_link_click: "Evento del link al sito",
  whatsapp_click: "Evento del passaggio a WhatsApp",
};

const daImpostazioni = (settings) => ({
  event_names: { ...EVENTI_PREDEFINITI, ...settings.event_names },
  actions: [...(settings.actions || [])],
  keys: { ...NOMI_PREDEFINITI, ...settings.keys }, // vuoto = il nome predefinito
  link_param: settings.link_param !== false,
  link_param_name: settings.link_param_name || "",
  link_param_value: settings.link_param_value || "",
});

/** Il problema di un nome scritto, o null se va bene (vuoto = il predefinito). */
function erroreNome(valore, max) {
  const nome = valore.trim();
  if (!nome) return null;
  if (!NOME_VALIDO.test(nome) || nome.length > max) {
    return `Solo lettere, numeri e _, iniziando con una lettera, fino a ${max} caratteri.`;
  }
  return null;
}

/**
 * Dettaglio dell'app "Google Analytics": quali interazioni il widget scrive
 * nel dataLayer del sito, con che nome di evento e di variabili, e se i link
 * interni prendono un parametro (di default chat_source=pingup). I valori
 * delle variabili li decide la piattaforma.
 * A destra cosa riceve il Tag Manager e come collegarlo a Google Analytics.
 * L'interruttore sta nell'intestazione della pagina.
 */
export default function DataLayerApp({ settings, limits, salva, saving, onAttivazione }) {
  const salvati = daImpostazioni(settings);
  const firmaSalvati = JSON.stringify(salvati);
  const [bozza, setBozza] = useState(salvati);
  useEffect(() => {
    setBozza(JSON.parse(firmaSalvati));
  }, [firmaSalvati]);

  const max = limits?.datalayer?.event_name_max || 40;
  const predefiniti = {
    eventi: { ...EVENTI_PREDEFINITI, ...settings.default_event_names },
    keys: { ...NOMI_PREDEFINITI, ...settings.default_keys },
    parametro: settings.default_link_param_name || "chat_source",
    valore: settings.default_link_param_value || "pingup",
  };

  // I nomi in uso (quelli scritti, o i predefiniti dove il campo e' vuoto).
  const eventi = Object.fromEntries(
    INTERAZIONI.map(({ id }) => [id, (bozza.event_names[id] || "").trim() || predefiniti.eventi[id]])
  );
  const nomi = Object.fromEntries(
    Object.keys(NOMI_PREDEFINITI).map((k) => [k, bozza.keys[k].trim() || predefiniti.keys[k]])
  );
  const parametro = bozza.link_param_name.trim() || predefiniti.parametro;
  const valore = bozza.link_param_value.trim() || predefiniti.valore;
  const accesa = (id) => bozza.actions.includes(id);
  const erroreEvento = (id) => erroreNome(bozza.event_names[id] || "", max);

  // I controlli, campo per campo.
  const errori = {
    // I nomi degli eventi: validi. Lo stesso per piu' interazioni va bene (le
    // distingue l'azione). Quello di un'interazione spenta non si vede, quindi
    // non blocca il salvataggio.
    ...Object.fromEntries(INTERAZIONI.map(({ id }) => [`evento_${id}`, accesa(id) ? erroreEvento(id) : null])),
    ...Object.fromEntries(
      Object.keys(NOMI_PREDEFINITI).map((k) => {
        const errore = erroreNome(bozza.keys[k], max);
        if (errore) return [k, errore];
        if (nomi[k] === "event") return [k, "«event» è riservato al nome dell'evento."];
        const doppio = Object.entries(nomi).some(([altra, nome]) => altra !== k && nome === nomi[k]);
        return [k, doppio ? "Due variabili con lo stesso nome: ognuna deve avere il suo." : null];
      })
    ),
    parametro: !PARAMETRO_VALIDO.test(parametro) || parametro.length > max
      ? "Solo lettere, numeri, _ e -, iniziando con una lettera."
      : PARAMETRI_RISERVATI.test(parametro)
        ? "Niente UTM o identificativi di campagna: cambierebbero la provenienza della visita in Analytics."
        : null,
    valore: !VALORE_VALIDO.test(valore) || valore.length > max ? "Solo lettere, numeri, _, - e punto." : null,
  };
  const tuttoValido = Object.values(errori).every((e) => !e);
  const conInterazioni = bozza.actions.length > 0;
  const modificato = JSON.stringify(bozza) !== firmaSalvati;

  const patch = (changes) => setBozza((prev) => ({ ...prev, ...changes }));
  const patchNome = (chiave, valore) => setBozza((prev) => ({ ...prev, keys: { ...prev.keys, [chiave]: valore } }));
  // Le interazioni restano nell'ordine dell'elenco, qualunque sia l'ordine dei clic.
  const scegli = (id, acceso) =>
    setBozza((prev) => ({
      ...prev,
      actions: INTERAZIONI.map((i) => i.id).filter((x) => (x === id ? acceso : prev.actions.includes(x))),
    }));

  // Un nome sbagliato rimasto sotto un'interazione spenta torna al predefinito,
  // altrimenti il server rifiuterebbe tutto per un campo che non si vede.
  const daSalvare = {
    ...bozza,
    event_names: Object.fromEntries(
      INTERAZIONI.map(({ id }) => [id, accesa(id) || !erroreEvento(id) ? bozza.event_names[id] : ""])
    ),
  };
  const salvaEAttiva = () =>
    salva({ datalayer: { ...daSalvare, enabled: true } }, "Google Analytics attivo sul widget");
  // Con almeno un'interazione e i nomi validi l'interruttore in alto accende e salva tutto.
  useAttivazione(onAttivazione, tuttoValido && conInterazioni, salvaEAttiva);

  return (
    <div className={`grid items-start ${SPAZI.campiAnteprima} xl:grid-cols-[minmax(0,1fr)_420px] 2xl:grid-cols-[minmax(0,1fr)_520px]`}>
      <div className="min-w-0 space-y-8">
        <Passi>
          <Gruppo
            fatto={(settings.actions || []).length > 0}
            titolo="Quali interazioni inviare"
            descrizione="Ogni interazione scelta arriva nel dataLayer della pagina, pronta per il tuo Tag Manager. Di serie tutte con l'evento pingup_interaction: sotto ognuna puoi scriverne un altro, se il tuo Tag Manager lo usa."
            className="space-y-4"
          >
            {INTERAZIONI.map((interazione) => (
              <div key={interazione.id} className="space-y-3">
                <RigaInterruttore
                  id={`gtm-${interazione.id}`}
                  etichetta={interazione.titolo}
                  aiuto={interazione.aiuto}
                  attivo={accesa(interazione.id)}
                  onCambia={(acceso) => scegli(interazione.id, acceso)}
                />
                {/* Come il parametro dei link: il nome dell'evento compare solo con l'interazione accesa. */}
                {accesa(interazione.id) ? (
                  <div className="grid max-w-xl gap-4 sm:grid-cols-2">
                    <Campo
                      id={`gtm-evento-${interazione.id}`}
                      etichetta={EVENTO_DI[interazione.id]}
                      aiuto={errori[`evento_${interazione.id}`] || undefined}
                    >
                      <Input
                        id={`gtm-evento-${interazione.id}`}
                        maxLength={max}
                        value={bozza.event_names[interazione.id]}
                        placeholder={predefiniti.eventi[interazione.id]}
                        aria-invalid={Boolean(errori[`evento_${interazione.id}`])}
                        onChange={(e) =>
                          setBozza((prev) => ({ ...prev, event_names: { ...prev.event_names, [interazione.id]: e.target.value } }))
                        }
                        className="font-mono"
                      />
                    </Campo>
                  </div>
                ) : null}
              </div>
            ))}
            {!conInterazioni ? (
              <p className="text-xs text-amber-700">Scegline almeno una per poter attivare l'app.</p>
            ) : null}
          </Gruppo>

          <Gruppo
            titolo="Nomi delle variabili"
            descrizione="Se il tuo Tag Manager usa già altri nomi. I valori restano gli stessi; lascia vuoto per i nomi predefiniti."
            facoltativo
            className="grid gap-4 sm:grid-cols-3"
          >
            {VARIABILI.map((variabile) => (
              <Campo
                key={variabile.chiave}
                id={`gtm-nome-${variabile.chiave}`}
                etichetta={variabile.etichetta}
                aiuto={errori[variabile.chiave] || `Per esempio: ${variabile.esempio}`}
              >
                <Input
                  id={`gtm-nome-${variabile.chiave}`}
                  maxLength={max}
                  value={bozza.keys[variabile.chiave]}
                  placeholder={predefiniti.keys[variabile.chiave]}
                  aria-invalid={Boolean(errori[variabile.chiave])}
                  onChange={(e) => patchNome(variabile.chiave, e.target.value)}
                  className="font-mono"
                />
              </Campo>
            ))}
          </Gruppo>

          <Gruppo
            titolo="Link verso le pagine del sito"
            descrizione="Per riconoscere sulla pagina di arrivo chi viene dal widget."
            facoltativo
            className="space-y-4"
          >
            <RigaInterruttore
              id="gtm-link"
              etichetta="Aggiungi un parametro ai link"
              aiuto={`Solo ai link verso il tuo sito, per esempio /offerte/?${parametro}=${valore}. Niente UTM: non cambiano la provenienza della visita in Google Analytics.`}
              attivo={bozza.link_param}
              onCambia={(link_param) => patch({ link_param })}
            />
            {bozza.link_param ? (
              <div className="grid max-w-xl gap-4 sm:grid-cols-2">
                <Campo id="gtm-parametro" etichetta="Nome del parametro" aiuto={errori.parametro || undefined}>
                  <Input
                    id="gtm-parametro"
                    maxLength={max}
                    value={bozza.link_param_name}
                    placeholder={predefiniti.parametro}
                    aria-invalid={Boolean(errori.parametro)}
                    onChange={(e) => patch({ link_param_name: e.target.value })}
                    className="font-mono"
                  />
                </Campo>
                <Campo id="gtm-valore" etichetta="Valore" aiuto={errori.valore || undefined}>
                  <Input
                    id="gtm-valore"
                    maxLength={max}
                    value={bozza.link_param_value}
                    placeholder={predefiniti.valore}
                    aria-invalid={Boolean(errori.valore)}
                    onChange={(e) => patch({ link_param_value: e.target.value })}
                    className="font-mono"
                  />
                </Campo>
              </div>
            ) : null}
          </Gruppo>
        </Passi>

        <BarraSalvataggio
          modificato={modificato}
          saving={saving}
          salvaDisabilitato={!tuttoValido}
          attiva={settings.enabled}
          puoAttivare={tuttoValido && conInterazioni}
          onSalva={() => salva({ datalayer: daSalvare }, "Impostazioni di Google Analytics salvate")}
          onSalvaEAttiva={salvaEAttiva}
        />
      </div>

      <CosaArriva
        // Il nome dell'evento di ogni interazione, come lo scrivera' il widget
        // (con un nome sbagliato nel campo, quello predefinito).
        eventoDi={(azione) => (errori[`evento_${azione}`] ? predefiniti.eventi[azione] : eventi[azione])}
        nomi={Object.fromEntries(Object.entries(nomi).map(([k, n]) => [k, errori[k] ? predefiniti.keys[k] : n]))}
        azioni={bozza.actions}
      />
    </div>
  );
}

/** L'interazione dell'esempio, fra quelle accese: il link se c'e', perche' mostra tutti i campi. */
const azioneEsempio = (azioni) => (azioni.includes("internal_link_click") ? "internal_link_click" : azioni[0] || "open");

function esempioPush(evento, nomi, azione) {
  const righe = [`  event: "${evento}",`, `  ${nomi.action}: "${azione}",`];
  if (azione === "internal_link_click") righe.push(`  ${nomi.destination_path}: "/offerte/",`);
  righe.push(`  ${nomi.position}: "floating_widget"`);
  return ["window.dataLayer.push({", ...righe, "});"].join("\n");
}

/** A destra: cosa riceve il Tag Manager e i tre passi per collegarlo a GA4. */
function CosaArriva({ eventoDi, nomi, azioni }) {
  const codice = (testo) => <code className="font-mono">{testo}</code>;
  const azione = azioneEsempio(azioni);
  // Gli eventi da mettere negli attivatori: quelli delle interazioni accese, una volta sola.
  const daAscoltare = [...new Set((azioni.length ? azioni : ["open"]).map(eventoDi))];
  const passi = [
    <>
      <b>{daAscoltare.length === 1 ? "Attivatore" : "Attivatori"}</b> «Evento personalizzato»
      {daAscoltare.length === 1 ? " su " : ", uno per evento: "}
      {daAscoltare.map((nome, i) => (
        <React.Fragment key={nome}>
          {i ? ", " : ""}
          {codice(nome)}
        </React.Fragment>
      ))}
      .
    </>,
    <>
      <b>Variabili del livello dati</b>: {codice(nomi.action)}, {codice(nomi.destination_path)} e{" "}
      {codice(nomi.position)}.
    </>,
    <>
      <b>Tag</b> «Google Analytics: evento GA4» {daAscoltare.length === 1 ? "su quell'attivatore" : "per ogni attivatore"}, con quei
      parametri: {codice(nomi.action)} dice cosa ha fatto il visitatore.
    </>,
  ];
  return (
    <aside className="space-y-2 xl:sticky xl:top-4">
      <HandNote>cosa riceve il tuo Tag Manager</HandNote>
      <div className="space-y-5 rounded-[14px] border border-[#E2E8F0] bg-white p-5 shadow-[0_10px_7.5px_rgba(0,0,0,0.05),0_4px_3px_rgba(0,0,0,0.05)]">
        <pre
          data-testid="gtm-esempio"
          className={`overflow-x-auto rounded-[10px] border border-[#E2E8F0] ${FONDO_CODICE} p-4 text-[13px] leading-6`}
        >
          <code className="font-mono text-[#020617]">{esempioPush(eventoDi(azione), nomi, azione)}</code>
        </pre>
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-[#020617]">In Google Tag Manager</h3>
          <ol data-testid="gtm-passi" className="space-y-2 text-sm leading-5 text-[#475569]">
            {passi.map((passo, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F1F5F9] text-xs font-semibold text-[#334155]">
                  {i + 1}
                </span>
                <span>{passo}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
      <div className="space-y-1 px-1 pt-1 text-xs leading-5 text-[#64748B]">
        <p>
          Il widget non manda niente a Google da solo: scrive nel dataLayer della pagina, e il Tag Manager
          del sito decide cosa inviare, anche in base al consenso ai cookie.
        </p>
        <p>Mai dati personali: nessun testo della chat, nome, email o numero di telefono.</p>
        <p>
          {codice(nomi.position)} vale {codice("floating_widget")} per la chat e {codice("search_bar")} per la barra
          di ricerca.
        </p>
      </div>
    </aside>
  );
}
